<template>
  <b-modal
    id="financial-batch-acknowledge-modal"
    :visible="acknowledgeModal.visible"
    centered
    size="lg"
    :no-close-on-backdrop="saving"
    :no-close-on-esc="saving"
    :hide-header-close="saving"
    data-testid="financial-batch-acknowledge-modal"
    @shown="onShow"
    @hidden="clear"
    @change="val => mutateAcknowledgeModal({ visible: val })"
  >
    <template #modal-title>
      Revisão da operação {{ acknowledgeModal.reference }}
    </template>

    <b-form-group label="Qual é a sua decisão?">
      <b-form-radio
        v-model="reviewStatus"
        name="financial-batch-review-status"
        :value="acknowledgedStatus"
        class="mb-50"
        data-testid="financial-batch-acknowledge-option-acknowledged"
      >
        Marcar como lido
        <small class="d-block text-muted">
          Você confirma que conferiu a alteração e que ela está de acordo.
        </small>
      </b-form-radio>
      <b-form-radio
        v-model="reviewStatus"
        name="financial-batch-review-status"
        :value="questionedStatus"
        data-testid="financial-batch-acknowledge-option-questioned"
      >
        Questionar
        <small class="d-block text-muted">
          Registra a discordância para que a TI retome a operação.
        </small>
      </b-form-radio>
    </b-form-group>

    <b-form-group
      :label="notesLabel"
      label-for="financial-batch-review-notes"
    >
      <b-form-textarea
        id="financial-batch-review-notes"
        v-model="reviewNotes"
        rows="4"
        :state="notesState"
        :disabled="saving"
        placeholder="Descreva o que foi conferido ou o que está em desacordo"
        data-testid="financial-batch-review-notes"
      />
      <div class="d-flex justify-content-between mt-25">
        <small
          v-if="notesError"
          class="text-danger"
          data-testid="financial-batch-review-notes-error"
        >
          {{ notesError }}
        </small>
        <small
          v-else
          class="text-muted"
        >
          {{ isQuestioning ? 'Observação obrigatória para questionar.' : 'Observação opcional.' }}
        </small>
        <small
          class="text-muted numeric"
          data-testid="financial-batch-review-notes-counter"
        >
          {{ notesLength }}{{ isQuestioning ? ' / ' + minNotesLength : '' }} caracteres
        </small>
      </div>
    </b-form-group>

    <b-alert
      :show="!!blockedMessage"
      variant="warning"
      class="mb-0"
      data-testid="financial-batch-acknowledge-blocked"
    >
      <div class="alert-body">
        <feather-icon
          icon="AlertTriangleIcon"
          class="mr-50"
        />
        {{ blockedMessage }}
      </div>
    </b-alert>

    <template #modal-footer>
      <b-button
        variant="outline-secondary"
        :disabled="saving"
        data-testid="financial-batch-acknowledge-cancel"
        @click="mutateAcknowledgeModal({ visible: false })"
      >
        Cancelar
      </b-button>
      <b-button
        :variant="isQuestioning ? 'danger' : 'primary'"
        :disabled="saving || !!notesError"
        data-testid="financial-batch-acknowledge-confirm"
        @click="submit"
      >
        <b-spinner
          v-if="saving"
          small
          class="mr-50"
        />
        {{ confirmLabel }}
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import {
  BModal, BButton, BFormGroup, BFormRadio, BFormTextarea, BAlert, BSpinner,
} from 'bootstrap-vue'
import { mapActions, mapGetters, mapMutations } from 'vuex'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent'
import * as types from '@/modules/financial_batch/store/types'
import {
  ACKNOWLEDGED_REVIEW_STATUS,
  QUESTIONED_REVIEW_STATUS,
  REVIEW_NOTES_MIN_LENGTH,
} from './financialBatchLabels'

// O backend responde 405 quando a regra recusa a leitura (lote já revisado, em simulação
// ou o próprio autor tentando revisar). Não é erro de sistema: a mensagem vem no corpo.
const RULE_REJECTED_STATUS = 405

export default {
  name: 'FinancialBatchAcknowledgeModal',
  components: {
    BModal,
    BButton,
    BFormGroup,
    BFormRadio,
    BFormTextarea,
    BAlert,
    BSpinner,
  },
  props: {
    initialReviewStatus: {
      type: String,
      default: ACKNOWLEDGED_REVIEW_STATUS,
    },
  },
  setup() {
    return { toast: useToast() }
  },
  data() {
    return {
      acknowledgedStatus: ACKNOWLEDGED_REVIEW_STATUS,
      questionedStatus: QUESTIONED_REVIEW_STATUS,
      minNotesLength: REVIEW_NOTES_MIN_LENGTH,
      reviewStatus: ACKNOWLEDGED_REVIEW_STATUS,
      reviewNotes: '',
      blockedMessage: undefined,
      saving: false,
    }
  },
  computed: {
    ...mapGetters({
      acknowledgeModal: types.FINANCIAL_BATCH_ACKNOWLEDGE_MODAL,
    }),
    isQuestioning() {
      return this.reviewStatus === QUESTIONED_REVIEW_STATUS
    },
    trimmedNotes() {
      return (this.reviewNotes || '').trim()
    },
    notesLength() {
      return this.trimmedNotes.length
    },
    notesLabel() {
      return this.isQuestioning ? 'Observação (obrigatória)' : 'Observação'
    },
    notesError() {
      if (!this.isQuestioning) {
        return undefined
      }
      if (!this.notesLength) {
        return 'Informe o motivo do questionamento.'
      }
      if (this.notesLength < this.minNotesLength) {
        const missing = this.minNotesLength - this.notesLength
        return `Descreva com pelo menos ${this.minNotesLength} caracteres (faltam ${missing}).`
      }
      return undefined
    },
    notesState() {
      if (!this.isQuestioning || !this.notesLength) {
        return null
      }
      return this.notesError ? false : null
    },
    confirmLabel() {
      if (this.saving) {
        return 'Aguarde...'
      }
      return this.isQuestioning ? 'Registrar questionamento' : 'Marcar como lido'
    },
  },
  methods: {
    ...mapMutations({
      mutateAcknowledgeModal: types.MUTATE_FINANCIAL_BATCH_ACKNOWLEDGE_MODAL,
    }),
    ...mapActions({
      acknowledgeBatch: types.ACKNOWLEDGE_FINANCIAL_BATCH,
    }),
    onShow() {
      this.reviewStatus = this.initialReviewStatus || ACKNOWLEDGED_REVIEW_STATUS
      this.reviewNotes = ''
      this.blockedMessage = undefined
    },
    submit() {
      if (this.notesError) {
        return
      }
      this.saving = true
      this.blockedMessage = undefined
      this.acknowledgeBatch({
        reference: this.acknowledgeModal.reference,
        review_status: this.reviewStatus,
        review_notes: this.trimmedNotes,
      })
        .then(() => {
          this.toast({
            component: ToastificationContent,
            props: {
              title: 'Sucesso!',
              text: this.isQuestioning
                ? 'Questionamento registrado com sucesso.'
                : 'Marcado como lido.',
              icon: 'CheckIcon',
              variant: 'success',
            },
          })
          this.acknowledgeModal.saveAction()
          this.mutateAcknowledgeModal({ visible: false })
        })
        .catch(err => {
          const response = err && err.response ? err.response : undefined
          if (response && response.status === RULE_REJECTED_STATUS) {
            const message = (response.data && response.data.message)
              ? response.data.message
              : 'Não foi possível registrar a revisão desta operação.'
            this.blockedMessage = message
            this.toast({
              component: ToastificationContent,
              props: {
                title: 'Revisão não permitida',
                text: message,
                icon: 'AlertTriangleIcon',
                variant: 'warning',
              },
            })
            return
          }
          this.toast({
            component: ToastificationContent,
            props: {
              title: 'Oops!',
              text: 'Ocorreu um erro ao registrar a revisão. Entre em contato com o setor de TI.',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
        .finally(() => {
          this.saving = false
        })
    },
    clear() {
      this.reviewStatus = ACKNOWLEDGED_REVIEW_STATUS
      this.reviewNotes = ''
      this.blockedMessage = undefined
      // O saveAction volta ao no-op junto com a reference: ele é uma closure sobre a
      // tela que abriu o modal e, se ficasse pendurado, a próxima leitura recarregaria
      // o lote anterior.
      this.mutateAcknowledgeModal({ reference: undefined, saveAction: () => {} })
    },
  },
}
</script>

<style lang="scss" scoped>
.numeric {
  font-variant-numeric: tabular-nums;
  font-feature-settings: "tnum";
}
</style>
